import { useMemo } from 'react'

import { alpha } from '@mui/material/styles'
import { ChartData, ChartOptions } from 'chart.js'
import { Chart } from 'react-chartjs-2'

import {
  SUPPLIER_ACTIVATION_CHART_HEIGHT,
  SUPPLIER_ACTIVATION_DAY_POINTS,
} from 'components/reports/Metrics/constants/SupplierActivation'
import { SupplierActivationPointDto } from 'generated/graphql'
import { activePointGuide, pointLabels } from 'helpers/supplierActivationChart.helpers'

import { LINE_STYLE } from '../components/chartConfig'

interface Props {
  points: SupplierActivationPointDto[]
  color: string
  milestoneVerb: string
  height?: number
}

export const SupplierActivationCurveChart = ({
  points,
  color,
  milestoneVerb,
  height = SUPPLIER_ACTIVATION_CHART_HEIGHT,
}: Props) => {
  const isEmpty = points.length === 0

  const data = useMemo<ChartData<'line', (number | null)[], string>>(
    () => ({
      labels: isEmpty
        ? SUPPLIER_ACTIVATION_DAY_POINTS.map(String)
        : points.map((point) => String(point.day)),
      datasets: [
        {
          ...LINE_STYLE,
          type: 'line' as const,
          label: 'Cumulative % of drivers',
          data: isEmpty
            ? SUPPLIER_ACTIVATION_DAY_POINTS.map(() => null)
            : points.map((point) => point.percentage),
          borderColor: color,
          backgroundColor: alpha(color, 0.12),
          pointBackgroundColor: color,
          pointHoverBorderColor: '#FFFFFF',
          pointHoverBorderWidth: 2,
          fill: true,
          clip: false,
        },
      ],
    }),
    [points, color, isEmpty],
  )

  const options = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      layout: { padding: { top: 20, right: 28, left: 8 } },
      scales: {
        x: {
          grid: { display: false },
          title: { display: true, text: 'Days since creation' },
          ticks: { maxRotation: 0 },
        },
        y: {
          beginAtZero: true,
          max: 100,
          title: { display: true, text: '% of drivers' },
          ticks: { callback: (value) => `${value}%` },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: !isEmpty,
          displayColors: false,
          backgroundColor: '#FFFFFF',
          borderColor: 'rgba(0, 0, 0, 0.12)',
          borderWidth: 1,
          cornerRadius: 8,
          padding: 12,
          caretPadding: 10,
          titleColor: 'rgba(0, 0, 0, 0.87)',
          titleFont: { size: 13, weight: 600 },
          titleMarginBottom: 8,
          bodyColor: color,
          bodyFont: { size: 20, weight: 700 },
          footerColor: 'rgba(0, 0, 0, 0.6)',
          footerFont: { size: 11, weight: 400 },
          footerMarginTop: 6,
          callbacks: {
            title: (items) => `${points[items[0].dataIndex].day} days`,
            label: (context) => `${Math.round(points[context.dataIndex].percentage)}%`,
            footer: (items) => {
              const point = points[items[0].dataIndex]

              return [
                `${point.converted} of ${point.eligible} eligible drivers`,
                `${milestoneVerb} within ${point.day} days`,
              ]
            },
          },
        },
      },
    }),
    [points, milestoneVerb, isEmpty, color],
  )

  return (
    <div style={{ height, position: 'relative' }}>
      <Chart<'line', (number | null)[], string>
        type='line'
        data={data}
        options={options}
        plugins={[pointLabels(color), activePointGuide(color)]}
      />
    </div>
  )
}
